import React, { useState } from "react";
import { RefreshCw, Ban, PackageSearch, ShieldCheck, Trash2, Trash } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DataTable, StatCard, TabBar, TableSkeleton, platformBadge, timeStr, num, PLATFORM_FILTER } from "./shared";

const BATCH_SIZE = 25;

export default function CancelView({ write }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("in_keep");
  const [confirm, setConfirm] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState({}); // { order_sn: true }
  const [bulk, setBulk] = useState(null); // { mode, items } confirmation
  const [progress, setProgress] = useState(null); // { done, total, cancelled, skipped, failed }

  const sync = async () => {
    setLoading(true);
    setSelected({});
    try {
      const r = await api.post("/desty/sync-cancel", { lookback_days: 35 });
      setData(r.data);
      const s = r.data.summary || {};
      setTab((s.in_keep || 0) > 0 ? "in_keep" : "not_keep");
      toast.success(`${s.total_cancel || 0} order cancel · ${s.in_keep || 0} ada di Keep`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doCancel = async () => {
    if (!confirm) return;
    setCancelling(true);
    try {
      const r = await api.post("/desty/cancel-keep", { keep_id: confirm.keep.id, order_sn: confirm.order_sn });
      toast.success(`Keep ${r.data.keep_number} dibatalkan — stok/kuota dilepas`);
      setConfirm(null);
      sync();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCancelling(false); }
  };

  const runAuto = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/desty/autocancel-run");
      let s = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/desty/autocancel-run/${data.job_id}`);
        if (st.data.status === "done") { s = st.data.result; break; }
        if (st.data.status === "error") throw new Error(st.data.error);
      }
      if (!s) throw new Error("Pemindaian Desty terlalu lama, coba lagi.");
      toast.success(`Auto Cancel selesai · ${s.cancelled || 0} keep dibatalkan · ${s.in_sale || 0} sudah jadi Sales · ${s.failed || 0} gagal`);
      await sync();
    } catch (e) {
      toast.error(e.response ? formatApiError(e.response?.data?.detail) : e.message);
    } finally { setRunning(false); }
  };

  const inKeepRows = data?.in_keep || [];
  const selectedRows = inKeepRows.filter((o) => selected[o.order_sn]);
  const allSelected = inKeepRows.length > 0 && selectedRows.length === inKeepRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleAll = (v) => {
    if (v) { const next = {}; inKeepRows.forEach((o) => { next[o.order_sn] = true; }); setSelected(next); }
    else setSelected({});
  };
  const toggleOne = (sn, v) => setSelected((s) => { const n = { ...s }; if (v) n[sn] = true; else delete n[sn]; return n; });

  const runBulk = async () => {
    if (!bulk) return;
    const items = bulk.items.map((o) => ({ keep_id: o.keep?.id, order_sn: o.order_sn })).filter((x) => x.keep_id);
    const total = items.length;
    setProgress({ done: 0, total, cancelled: 0, skipped: 0, failed: 0 });
    setBulk(null);
    let done = 0, cancelled = 0, skipped = 0, failed = 0;
    try {
      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const chunk = items.slice(i, i + BATCH_SIZE);
        try {
          const r = (await api.post("/desty/cancel-keep-bulk", { items: chunk })).data || {};
          cancelled += r.cancelled || 0; skipped += r.skipped || 0; failed += r.failed || 0;
        } catch (e) {
          failed += chunk.length;
        }
        done += chunk.length;
        setProgress({ done, total, cancelled, skipped, failed });
      }
      toast.success(`Selesai · ${cancelled} dibatalkan · ${skipped} dilewati · ${failed} gagal`);
    } finally {
      setTimeout(() => setProgress(null), 1200);
      await sync();
    }
  };

  const s = data?.summary || {};
  const rows = data ? (data[tab] || []) : [];
  const TABS = [
    { key: "in_keep", label: "Sudah di Keep ", icon: PackageSearch, count: data?.in_keep?.length || 0 },
    { key: "not_keep", label: "Tidak ada Keep ", icon: ShieldCheck, count: data?.not_keep?.length || 0 },
  ];

  const selectCol = {
    key: "sel",
    header: write ? (
      <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
        onCheckedChange={toggleAll} aria-label="Pilih semua" data-testid="desty-cancel-selectall" />
    ) : "",
    render: (o) => write ? (
      <Checkbox checked={!!selected[o.order_sn]} onCheckedChange={(v) => toggleOne(o.order_sn, v)}
        aria-label={`Pilih ${o.order_sn}`} data-testid={`desty-cancel-select-${o.order_sn}`} />
    ) : null,
  };
  const base = [
    { key: "order_sn", header: "Order ID (Desty)", className: "font-mono text-xs", sortAccessor: (o) => o.order_sn },
    { key: "platform", header: "Platform", render: (o) => platformBadge(o.platform) },
    { key: "store_name", header: "Toko", className: "text-sm", render: (o) => o.store_name || "-" },
    { key: "update_time", header: "Tgl Cancel", className: "text-sm", sortAccessor: (o) => o.update_time || 0, render: (o) => timeStr(o.update_time) },
  ];
  const inKeepCols = [
    { key: "keep_number", header: "No Keep", className: "font-medium", render: (o) => o.keep?.number },
    { key: "customer", header: "Customer", className: "text-sm", render: (o) => o.keep?.customer_name },
    { key: "qty", header: "Qty", align: "right", sortAccessor: (o) => o.keep?.remaining_qty || 0, render: (o) => num(o.keep?.remaining_qty) },
    { key: "aksi", header: "Aksi", align: "right", render: (o) => (
      write ? <Button size="sm" variant="destructive" onClick={() => setConfirm(o)} className="h-8" data-testid={`desty-cancelkeep-${o.order_sn}`}>
        <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel Keep</Button> : null
    ) },
  ];
  const notKeepCols = [{ key: "status", header: "Status", render: () => <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Tidak ada keep — aman</Badge> }];
  const columns = tab === "in_keep" ? [...(write ? [selectCol] : []), ...base, ...inKeepCols] : [...base, ...notKeepCols];

  const bulkActions = (write && tab === "in_keep" && inKeepRows.length > 0) ? (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
        disabled={selectedRows.length === 0 || !!progress}
        onClick={() => setBulk({ mode: "selected", items: selectedRows })} data-testid="desty-cancel-bulk-selected">
        <Trash className="w-3.5 h-3.5 mr-1" /> Batalkan Terpilih ({selectedRows.length})
      </Button>
      <Button size="sm" variant="destructive" disabled={!!progress}
        onClick={() => setBulk({ mode: "all", items: inKeepRows })} data-testid="desty-cancel-bulk-all">
        <Trash className="w-3.5 h-3.5 mr-1" /> Batalkan Semua ({inKeepRows.length})
      </Button>
    </div>
  ) : null;

  return (
    <>
      <div className="flex justify-end gap-2">
        <Button onClick={runAuto} disabled={running || loading} variant="outline" className="border-cocoa text-cocoa" data-testid="desty-cancel-run-btn" title="Cek pembatalan (35 hari terakhir) & batalkan Keep terkait otomatis">
          <Ban className={`w-4 h-4 mr-1.5 ${running ? "animate-pulse" : ""}`} />
          {running ? "Memproses…" : "Jalankan Auto Cancel"}
        </Button>
        <Button onClick={sync} disabled={loading || running} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="desty-cancel-sync-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Menarik data..." : "Sync Cancel"}
        </Button>
      </div>

      {loading && <TableSkeleton testid="desty-cancel-skeleton" />}

      {!loading && !data && (
        <Panel className="p-10 text-center text-cocoa/60" data-testid="desty-cancel-empty">
          <Ban className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Klik <b>Sync Cancel</b> untuk menarik order yang dibatalkan dari Desty.
        </Panel>
      )}

      {!loading && data && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Total Cancel" value={s.total_cancel} tone="cocoa" />
            <StatCard label="Sudah di Keep" value={s.in_keep} tone="amber" />
            <StatCard label="Tidak ada Keep" value={s.not_keep} tone="green" />
          </div>
          <TabBar tabs={TABS} value={tab} onChange={setTab} prefix="desty-ctab" />
          {progress && (
            <Panel className="p-4 space-y-2" data-testid="desty-cancel-progress">
              <div className="flex justify-between text-sm text-cocoa">
                <span>Membatalkan keep… {progress.done}/{progress.total}</span>
                <span className="text-cocoa/60">{num(progress.cancelled)} ok · {num(progress.skipped)} dilewati · {num(progress.failed)} gagal</span>
              </div>
              <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
            </Panel>
          )}
          <DataTable testid="desty-cancel-table" columns={columns} rows={rows}
            searchAccessors={[(o) => o.order_sn, (o) => o.store_name, (o) => o.keep?.number, (o) => o.keep?.customer_name]}
            searchPlaceholder="Cari Order ID / toko / no keep / customer…" filters={[PLATFORM_FILTER]}
            rightSlot={bulkActions}
            emptyText="Tidak ada data pada kategori ini." />
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="desty-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel Keep</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-2 text-sm">
              <p>Order Desty <b className="font-mono">{confirm.order_sn}</b> ({confirm.platform}) dibatalkan. Keep terkait akan di-<b>Cancel</b> dan reservasi stok / kuota PO dilepas.</p>
              <div className="rounded-lg bg-cocoa/5 p-3 space-y-1">
                <div className="flex justify-between"><span className="text-cocoa/60">No Keep</span><b>{confirm.keep?.number}</b></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Customer</span><span>{confirm.keep?.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Total Qty</span><span>{num(confirm.keep?.remaining_qty)} pcs</span></div>
                <div className="flex justify-between"><span className="text-cocoa/60">Bucket</span><span>{confirm.keep?.sales_bucket}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="desty-cancel-cancel">Batal</Button>
            <Button variant="destructive" onClick={doCancel} disabled={cancelling} data-testid="desty-cancel-ok">
              {cancelling ? "Memproses..." : "Ya, Cancel Keep"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bulk} onOpenChange={(v) => !v && setBulk(null)}>
        <DialogContent data-testid="desty-cancel-bulk-dialog">
          <DialogHeader><DialogTitle>Batalkan Keep Massal</DialogTitle></DialogHeader>
          {bulk && (
            <div className="space-y-2 text-sm">
              <p>Yakin membatalkan <b>{num(bulk.items.length)}</b> keep{bulk.mode === "all" ? " (semua order batal yang ada di Keep)" : " terpilih"}?</p>
              <p className="text-cocoa/70">Reservasi stok / kuota PO untuk semua keep tersebut akan <b>dilepas</b>. Aksi ini tidak bisa dibatalkan.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(null)} data-testid="desty-cancel-bulk-cancel">Batal</Button>
            <Button variant="destructive" onClick={runBulk} data-testid="desty-cancel-bulk-ok">
              Ya, Batalkan {bulk ? num(bulk.items.length) : ""} Keep
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
